import { Badge, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBooks, faGlobe } from '@fortawesome/pro-regular-svg-icons';
import { ButtonText } from '@morressier/alembic';
import { ISearchQueryItems } from 'src/api/queries';
import { getRScoreBadgeVariant } from 'src/utils/badgeVariants';
import { ILexiconField } from '@publishing-engine/domain/models';
import LabelWithTooltip from './LabelWithTooltip';
import { getReviewerPropertyValue } from 'src/utils/reviews';

interface MetricDefinition {
  key: string;
  config: ILexiconField;
}

interface Props {
  item: ISearchQueryItems;
  visibleMetrics: MetricDefinition[];
  collapsedMetrics: MetricDefinition[];
  globalLexiconConfig: boolean | undefined;
}

const ReviewerMetricsGrid = ({
  item,
  visibleMetrics,
  collapsedMetrics,
  globalLexiconConfig
}: Props) => {
  const [showMore, setShowMore] = useState(false);

  return (
    <>
      <Grid templateColumns="repeat(3, 1fr)" gap={2}>
        {visibleMetrics.map(({ key, config }) => {
          const rawValue = getReviewerPropertyValue(item.properties, key);
          const globalValue = getReviewerPropertyValue(
            item.globalReviewer,
            key
          );
          let localContent;
          let globalContent;

          if (key === 'AverageRScore') {
            const { variant, label } = getRScoreBadgeVariant(rawValue);
            const { variant: globalVariant, label: globalLabel } =
              getRScoreBadgeVariant(globalValue);
            localContent = (
              <Badge variant={variant}>
                {label ? `${rawValue} (${label})` : rawValue}
              </Badge>
            );
            globalContent = (
              <Badge variant={globalVariant}>
                {globalLabel ? `${globalValue} (${globalLabel})` : globalValue}
              </Badge>
            );
          } else {
            localContent = (
              <Text textStyle="textContext">{rawValue ?? '-'}</Text>
            );
            globalContent = (
              <Text textStyle="textContext">{globalValue ?? '-'}</Text>
            );
          }

          return (
            <GridItem
              key={key}
              display="flex"
              flexDirection="column"
              alignItems="stretch"
              gap={1}
            >
              <LabelWithTooltip config={config} />
              <Flex align="center" gap={2}>
                <Flex align="center" gap={1}>
                  <FontAwesomeIcon size="sm" icon={faBooks} />
                  {localContent}
                </Flex>
                {globalLexiconConfig && (
                  <>
                    <Text color="gray.500">|</Text>
                    <Flex align="center" gap={1}>
                      <FontAwesomeIcon size="sm" icon={faGlobe} />
                      {globalContent}
                    </Flex>
                  </>
                )}
              </Flex>
            </GridItem>
          );
        })}

        {showMore &&
          collapsedMetrics.map(({ key, config }) => {
            const journalValue = getReviewerPropertyValue(item.properties, key);
            const globalValue = getReviewerPropertyValue(
              item.globalReviewer,
              key
            );
            let journalContent;
            let globalContent;

            if (key === 'AverageRScore') {
              const { variant, label } = getRScoreBadgeVariant(journalValue);
              const { variant: globalVariant, label: globalLabel } =
                getRScoreBadgeVariant(globalValue);
              journalContent = (
                <Badge variant={variant}>
                  {label ? `${journalValue} (${label})` : journalValue}
                </Badge>
              );
              globalContent = (
                <Badge variant={globalVariant}>
                  {globalLabel ? `${globalValue} (${globalLabel})` : globalValue}
                </Badge>
              );
            } else {
              journalContent = (
                <Text textStyle="textContext" paddingLeft={2}>
                  {journalValue}
                </Text>
              );
              globalContent = (
                <Text textStyle="textContext" paddingLeft={2}>
                  {globalValue}
                </Text>
              );
            }

            return (
              <GridItem
                key={key}
                display="flex"
                flexDirection="column"
                alignItems="stretch"
                gap={1}
              >
                <LabelWithTooltip config={config} />
                <Flex
                  alignItems={key.includes('Date') ? 'flex-start' : 'center'}
                  gap={2}
                  direction={key.includes('Date') ? 'column' : 'row'}
                >
                  <Flex alignItems="center" gap={1}>
                    <FontAwesomeIcon size="sm" icon={faBooks} />
                    {journalContent}
                  </Flex>
                  {key.includes('Date')
                    ? null
                    : globalLexiconConfig && <Text color="gray.500">|</Text>}
                  {globalLexiconConfig && (
                    <Flex alignItems="center" gap={1}>
                      <FontAwesomeIcon size="sm" icon={faGlobe} />
                      {globalContent}
                    </Flex>
                  )}{' '}
                </Flex>
              </GridItem>
            );
          })}
      </Grid>

      <Flex paddingBottom={2}>
        <ButtonText size="regular" onClick={() => setShowMore(prev => !prev)}>
          {showMore ? 'Show less' : 'Show more...'}
        </ButtonText>
      </Flex>
    </>
  );
};

export default ReviewerMetricsGrid;
